import { type FC } from 'react';
import { Controller } from 'react-hook-form';
import {
  Autocomplete,
  Box,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  Stack,
  TextField,
} from '@mui/material';

import { getFormErrorAttributes } from '#apps/client-web/app/helpers/react-hook-form';
import {
  AttributeLabel,
  ColorTemperatureLabel,
  FixtureTypeLabel,
  LampBaseLabel,
  LampTypeLabel,
  LightSourceLabel,
} from '#apps/client-web/features/audit-form-lighting-type/constants';
import { FileInput } from '#apps/client-web/features/file-storage/components/file-input';

import {
  type Attribute,
  type ColorTemperature,
  type FixtureType,
  type LampBase,
  type LampType,
  type LightSource,
} from '#packages/data-collection/models/audit-lighting-type-form';

import { type EditAuditFormLightingTypeEntry } from './types';

export type AuditFormLightingTypeEntryPageRendererProps = {
  form: EditAuditFormLightingTypeEntry;
  isPending: boolean;
  onSubmit: () => void;
};

export const AuditFormLightingTypeEntryPageRenderer: FC<
  AuditFormLightingTypeEntryPageRendererProps
> = ({ form, isPending, onSubmit }) => {
  const { control } = form;
  const disableForm = isPending;

  return (
    <Box
      component="form"
      onSubmit={onSubmit}
      display="flex"
      flexDirection="column"
      flex={1}>
      <Stack spacing={4} sx={{ mt: 2 }} flex={1}>
        {/* Quantity + Light Source */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Controller
              control={control}
              name="quantity"
              disabled={disableForm}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  {...getFormErrorAttributes(fieldState.error)}>
                  <TextField
                    {...field}
                    {...getFormErrorAttributes(fieldState.error)}
                    label="No. of lamps"
                    type="number"
                    variant="outlined"
                  />
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 8 }}>
            <Controller
              control={control}
              name="lightSource"
              disabled={disableForm}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  {...getFormErrorAttributes(fieldState.error)}>
                  <Autocomplete
                    {...field}
                    {...getFormErrorAttributes(fieldState.error)}
                    options={Object.keys(LightSourceLabel)}
                    getOptionLabel={(option) =>
                      LightSourceLabel[option as LightSource]
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Light Source"
                        variant="outlined"
                        {...getFormErrorAttributes(fieldState.error)}
                      />
                    )}
                    value={field.value ?? null}
                    onChange={(_, value) => field.onChange(value)}
                  />
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
        </Grid>

        {/* Lamp Type + Lamp Wattage */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 7 }}>
            <Controller
              control={control}
              name="lampType"
              disabled={disableForm}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  {...getFormErrorAttributes(fieldState.error)}>
                  <Autocomplete
                    {...field}
                    {...getFormErrorAttributes(fieldState.error)}
                    options={Object.keys(LampTypeLabel)}
                    getOptionLabel={(option) =>
                      LampTypeLabel[option as LampType]
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Lamp Type"
                        variant="outlined"
                        {...getFormErrorAttributes(fieldState.error)}
                      />
                    )}
                    value={field.value ?? null}
                    onChange={(_, value) => field.onChange(value)}
                  />
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 5 }}>
            <Controller
              control={control}
              name="lampWattage"
              disabled={disableForm}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  {...getFormErrorAttributes(fieldState.error)}>
                  <TextField
                    {...field}
                    {...getFormErrorAttributes(fieldState.error)}
                    label="Lamp Wattage"
                    type="number"
                    variant="outlined"
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">W</InputAdornment>
                        ),
                      },
                    }}
                  />
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
        </Grid>

        {/* Lamp Base */}
        <Controller
          control={control}
          name="lampBase"
          disabled={disableForm}
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              {...getFormErrorAttributes(fieldState.error)}>
              <Autocomplete
                {...field}
                {...getFormErrorAttributes(fieldState.error)}
                options={Object.keys(LampBaseLabel)}
                getOptionLabel={(option) => LampBaseLabel[option as LampBase]}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Lamp Base"
                    variant="outlined"
                    {...getFormErrorAttributes(fieldState.error)}
                  />
                )}
                value={field.value ?? null}
                onChange={(_, value) => field.onChange(value)}
              />
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />

        {/* Fixture Type + Size + Mount */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Controller
              control={control}
              name="fixtureType"
              disabled={disableForm}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  {...getFormErrorAttributes(fieldState.error)}>
                  <Autocomplete
                    {...field}
                    {...getFormErrorAttributes(fieldState.error)}
                    options={Object.keys(FixtureTypeLabel)}
                    getOptionLabel={(option) =>
                      FixtureTypeLabel[option as FixtureType]
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Fixture Type"
                        variant="outlined"
                        {...getFormErrorAttributes(fieldState.error)}
                      />
                    )}
                    value={field.value ?? null}
                    onChange={(_, value) => field.onChange(value)}
                  />
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Controller
              control={control}
              name="fixtureSize"
              disabled={disableForm}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  {...getFormErrorAttributes(fieldState.error)}>
                  <TextField
                    {...field}
                    {...getFormErrorAttributes(fieldState.error)}
                    label="Fixture Size"
                    type="text"
                    variant="outlined"
                  />
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Controller
              control={control}
              name="fixtureMount"
              disabled={disableForm}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  {...getFormErrorAttributes(fieldState.error)}>
                  <TextField
                    {...field}
                    {...getFormErrorAttributes(fieldState.error)}
                    label="Fixture Mount"
                    type="text"
                    variant="outlined"
                  />
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
        </Grid>

        {/* Attributes */}
        <Controller
          control={control}
          name="attributes"
          disabled={disableForm}
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              {...getFormErrorAttributes(fieldState.error)}>
              <Autocomplete
                {...field}
                {...getFormErrorAttributes(fieldState.error)}
                options={Object.keys(AttributeLabel)}
                multiple
                getOptionLabel={(option) => AttributeLabel[option as Attribute]}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Attribute"
                    variant="outlined"
                    {...getFormErrorAttributes(fieldState.error)}
                  />
                )}
                value={field.value}
                onChange={(_, value) => field.onChange(value)}
              />
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />

        {/* Color Temperature + Ballast Factor + Fixture Wattage */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Controller
              control={control}
              name="colorTemperature"
              disabled={disableForm}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  {...getFormErrorAttributes(fieldState.error)}>
                  <Autocomplete
                    {...field}
                    {...getFormErrorAttributes(fieldState.error)}
                    options={Object.keys(ColorTemperatureLabel)}
                    getOptionLabel={(option) =>
                      ColorTemperatureLabel[option as ColorTemperature]
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Color Temperature"
                        variant="outlined"
                        {...getFormErrorAttributes(fieldState.error)}
                      />
                    )}
                    value={field.value ?? null}
                    onChange={(_, value) => field.onChange(value)}
                  />
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Controller
              control={control}
              name="ballastFactor"
              disabled={disableForm}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  {...getFormErrorAttributes(fieldState.error)}>
                  <TextField
                    {...field}
                    {...getFormErrorAttributes(fieldState.error)}
                    label="Ballast Factor"
                    type="number"
                    variant="outlined"
                  />
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Controller
              control={control}
              name="fixtureWattage"
              disabled={disableForm}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  {...getFormErrorAttributes(fieldState.error)}>
                  <TextField
                    {...field}
                    {...getFormErrorAttributes(fieldState.error)}
                    label="Fixture Wattage"
                    type="number"
                    variant="outlined"
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">W</InputAdornment>
                        ),
                      },
                    }}
                  />
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
        </Grid>

        {/* Luminaire Name */}
        <Controller
          control={control}
          name="luminaireName"
          disabled
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              {...getFormErrorAttributes(fieldState.error)}>
              <TextField
                {...field}
                {...getFormErrorAttributes(fieldState.error)}
                disabled
                label="Luminaire Name"
                type="text"
                variant="outlined"
              />
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />

        {/* Comments */}
        <Controller
          control={control}
          name="comments"
          disabled={disableForm}
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              {...getFormErrorAttributes(fieldState.error)}>
              <TextField
                {...field}
                {...getFormErrorAttributes(fieldState.error)}
                label="Comments"
                type="text"
                multiline
                rows={3}
                variant="outlined"
              />
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />

        {/* Attachments */}
        <Controller
          control={control}
          name="attachments"
          disabled={disableForm}
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              {...getFormErrorAttributes(fieldState.error)}>
              <FileInput
                {...field}
                {...getFormErrorAttributes(fieldState.error)}
                label="Attachments"
              />
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />
      </Stack>
    </Box>
  );
};
